/* eslint-disable max-lines */
import { useState, useRef, useEffect, createRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import H1 from '@base/H1/H1';
import { useSnackbar } from '@stores/snackbar';
import { useUpdateSelectedGoal } from '@api/hooks/topology/useSelectedGoal';
import pushRoute from '@utils/pushRoute';
import { getSubscription, hasActiveSubscriptionToGoal } from '@models/Me';
import { segment } from '@actions/analytics';
import { getImage } from '@utils/imageHelper';
import {
  ONBOARDING_LANGUAGE_SELECTED,
  ONBOARDING_GOAL_GROUP_SELECTED,
  ONBOARDING_GOAL_PAGE_SCROLLED,
  GOAL_SEARCH_SEARCH_CLICKED,
  GOAL_SEARCH_RESULT_CLICKED,
  ONBOARDING_GOAL_SELECTED,
  GOAL_SEARCH_NO_RESULT
} from '@cont/Onboarding/constants';
import Dynamic from '@base/Dynamic/Dynamic';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import { Fonts, Spacings, mediaQuery, breakpoints } from '@styles/index';
import { goalLevelOnboardingStatus } from '@constants/index';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import useWindowSize from '@hooks/useWindowSize';
import useFollowedGoals from '@api/hooks/goal/useFollowedGoals';
import Tooltip from '@base/Tooltip/Tooltip';
import Close from '@icon/Close';
import uuid from '@utils/uuid';
import { getBusinessPlatform } from '@cont/BatchGroup/utils';
import SearchTab from '@comp/SearchTab/SearchTab';
import useGoalLanguages from '@api/hooks/goal/useGoalLanguages';
import { fetchGoalLanguages } from '@api/fetchers/home';
import { followGoal } from '@api/fetchers/goal/updaters';
import { getIsLoggedIn, getMe } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useGoalSynonyms from '@api/hooks/home/useGoalSynonyms';
import { setGoalLanguage } from '@api/fetchers/auth';
import { isValidGoalOption } from './utils';
import Header from './ManageGoalHeader';
import MobileGoalGroupNav from './MobileGoalGroupNav';
import WebGoalGroups from './WebGoalGroups';
import NoResultFound from './search/NoResultFound';
import DropdownOptionList from './search/DropdownOptionList';

const LanguageSelectionDrawer = Dynamic(() =>
  import('@comp/LanguageSelectionDrawer/LanguageSelectionDrawer')
);

const Container = styled.div`
  width: 1136px;
  margin: 155px auto 0 auto;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  gap: 16px;
  position: relative;
  ${mediaQuery.uptoTablet} {
    width: 728px;
    margin-top: 83px;
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: 0px 16px;
    display: flex;
    flex-direction: column;
    gap: 0;
    background-color: var(--color-base-1);
    margin-top: 0;
  }
  &.no-result {
    grid-template-columns: unset;
    justify-content: center;
  }
`;

const PageHeader = styled.div`
  z-index: 1;
  border-bottom: 1px solid var(--color-divider);
  ${({ $isDarkModeOn }) =>
    !$isDarkModeOn
      ? 'background-color: var(--color-i-white);'
      : 'background: var(--color-i-black-1);'}
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_0B};
  }
  ${mediaQuery.uptoMobile} {
    padding: 0px;
    width: 100%;
    z-index: 3;
    display: ${({ $hide }) => ($hide ? 'none' : 'block')};
    background: var(--color-base-0);
    border-bottom: none;
    ${({ $isFreeTrial }) =>
      $isFreeTrial && `padding-top: ${Spacings.SPACING_0B}`}
  }
`;

const HeaderText = styled(H1)`
  font-size: 40px;
  line-height: 48px;
  text-align: center;
  padding-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoTablet} {
    font-size: 28px;
  }
  ${mediaQuery.uptoMobile} {
    font-size: 22px;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: auto;
  margin: auto;
  text-align: center;
  ${mediaQuery.uptoMobile} {
    height: auto;
    padding-top: ${Spacings.SPACING_0B};
  }
`;

const WrapperWithBg = styled(Wrapper)`
  width: 100%;
  height: 208px;
  display: flex;
  position: relative;
  ::before {
    content: ' ';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    ${(props) =>
      props.imageURL && `background-image: url('${getImage(props.imageURL)}');`}
    background-position: bottom right;
    background-repeat: no-repeat;
    background-size: cover;
    ${mediaQuery.uptoMobile} {
      ${(props) => props.searchInFocus && 'background: none;'}
    }
  }
  text-align: center;
  transition: background 1s ease-in-out;
  ${mediaQuery.uptoMobile} {
    height: ${(props) => (props.searchInFocus ? '72px' : '101px')};
  }
`;

const InnerWrapper = styled.div`
  max-width: 1136px;
  margin: auto;
  text-align: left;
  position: relative;
  z-index: 1;
  ${mediaQuery.uptoTablet} {
    max-width: 728px;
  }
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const GoalSearchBarWrapper = styled.div`
  display: flex;
  position: sticky;
  z-index: 3;
  justify-content: center;
  margin-inline: auto;
  width: 42%;
  top: ${Spacings.SPACING_2B};
  > div {
    transition: transform 200ms ease-out;
  }
  ${mediaQuery.uptoTablet} {
    margin-bottom: 0px;
    width: 60%;
  }
  ${mediaQuery.uptoMobile} {
    top: ${Spacings.SPACING_0B};
    width: 100%;
    padding: ${Spacings.SPACING_4B};
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 520px;
  height: 56px;
  justify-content: space-between;
  background: var(--color-base-1);
  border-radius: ${({ showBorderRadius }) =>
    `${showBorderRadius ? '6px' : '6px 6px 0px 0px'}`};
  border: ${({ showBorder }) =>
    `${showBorder ? '1px solid var(--color-divider)' : '0px'}`};

  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding-block: ${Spacings.SPACING_2B};
    border: ${({ $isFreeTrial }) =>
      `${Spacings.SPACING_1} solid ${
        $isFreeTrial ? 'var(--color-text-primary)' : 'var(--color-divider)'
      }`};
  }
  .search-dd-input-wrapper {
    padding-inline: ${Spacings.SPACING_4B};
    input {
      padding-left: 16px;
    }
    ${mediaQuery.uptoMobile} {
      padding-inline: ${Spacings.SPACING_1B};
      input {
        padding-left: ${Spacings.SPACING_2};
      }
    }
  }
  .search-dd-input {
    color: var(--color-text-primary);
    font-size: 14px;
    background: transparent;
    font-weight: ${Fonts.SEMIBOLD};
    ${mediaQuery.uptoTablet} {
      font-size: ${({ $isFreeTrial }) =>
        $isFreeTrial ? '14px' : Spacings.SPACING_3B};
      font-weight: ${Fonts.REGULAR};
    }
    ::placeholder {
      color: var(--color-text-secondary);
    }
  }
  .dropdown {
    top: 107%;
    border-top: 1px solid var(--color-divider);
    ${mediaQuery.uptoMobile} {
      display: none;
    }
  }

  ${mediaQuery.uptoMobile} {
    height: 48px;
    border: solid ${Spacings.SPACING_1} var(--color-text-primary);
  }
`;

const StyledTooltip = styled(Tooltip)`
  position: relative;
  transition: opacity linear 200ms;
  z-index: 980;
  &.MuiTooltip-popper {
    margin: 0;
    ${mediaQuery.uptoMobile} {
      margin: 0;
      ${({ $isFreeTrial }) =>
        $isFreeTrial && `top: ${Spacings.SPACING_7B} !important`}
    }
  }
  & {
    .tooltip {
      margin-top: 10px;
      background-color: var(--color-base-fill);
      @media only screen and (max-width: ${breakpoints.mobile}) {
        padding: 4px 8px;
        margin-top: 10px;
      }
    }
    .MuiTooltip-arrow {
      ::before {
        color: var(--color-base-fill) !important;
      }
    }
    .hide {
      opacity: 0;
    }
  }
  .search-dd-wrapper {
    position: static;
  }
  .search-dd-options {
    top: 110% !important;
  }
`;

const TooltipContainer = styled.div`
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_2B};
  width: 280px;
`;

const TooltipText = styled.div`
  font-weight: semibold;
  font-size: ${Fonts.H5};
  line-height: 150%;
  color: var(--color-text-tertiary);
`;

const CloseContainer = styled.div`
  position: absolute;
  top: ${Spacings.SPACING_4B};
  right: ${Spacings.SPACING_4B};
  cursor: pointer;
  font-size: ${Fonts.H5};
  svg {
    height: ${Spacings.SPACING_6B};
    width: ${Spacings.SPACING_6B};
  }
`;

const SearchTooltip = ({ closeTooltip }) => {
  return (
    <TooltipContainer>
      <CloseContainer onClick={closeTooltip}>
        <Close color="var(--color-text-tertiary)" />
      </CloseContainer>
      <TooltipText>Try typing your goal name</TooltipText>
    </TooltipContainer>
  );
};

const Overlay = styled.div`
  display: ${({ show }) => (show ? 'block' : 'contents')};
  position: fixed; /* Stay in place */
  z-index: 1; /* Sit on top */
  left: 0;
  top: 0;
  width: 100%; /* Full width */
  height: 100%; /* Full height */
  overflow: auto; /* Enable scroll if needed */
  background-color: var(--color-i-pitch-black); /* Black w/ opacity */
  opacity: 0.4;
  ${mediaQuery.uptoMobile} {
    display: contents;
  }
`;
const MobileGoalGroupNavWrapper = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: block;
  }
`;
const GoalGroupNavWrapper = styled.div`
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const getMappedFollowedGoals = (followedGoals = []) => {
  return followedGoals.reduce((mappedFollowedGoals, followedGoal) => {
    return { ...mappedFollowedGoals, [followedGoal.uid]: followedGoal };
  }, {});
};

const ManageGoals = ({
  goalGroupsData,
  popularGoalGroup,
  isOnboarding,
  onGoalSelection,
  isFreeTrial,
  popularGoalOptions
}) => {
  const allGoalGroups = goalGroupsData;
  const filteredGoalGroups = goalGroupsData;
  const [filteredGoalOptions, setFilteredGoalOptions] = useState(allGoalGroups);
  const [drawerState, setDrawerState] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [searchInFocus, setSearchInFocus] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showSearchTooltip, setShowSearchTooltip] = useState(false);
  const [currentRefIndex, setCurrentRefIndex] = useState(0);
  const [isLoadingFirst, setIsLoadingFirst] = useState(false);
  const [goalSearchValue, setGoalSearchValue] = useState('');
  const [showOverlay, setOverlay] = useState(false);

  const searchCount = useRef(0);
  const tpID = useRef(0);
  const lastScrollTop = useRef(0);
  const scrollTop = useRef(0);
  const searchBarRef = useRef(null);
  const goalSelectedSection = useRef(null);
  const goalGroupRefs = useRef(allGoalGroups.map(createRef));
  const scrollTracked = useRef(false);

  const {
    query: { topologyUid = '' }
  } = useRouter();
  const { isMobile } = useWindowSize();
  const me = useMyInfo(getMe);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const { open: showSnackbar } = useSnackbar();

  const { languages: goalLanguages } = useGoalLanguages(selectedGoal?.uid);
  const { goalSynonyms } = useGoalSynonyms();
  const { latestUserSubscriptions } = me || {};

  const { followedGoals, mutate: fetchFollowedGoals } = useFollowedGoals();
  const mappedFollowedGoals = useMemo(
    () => getMappedFollowedGoals(followedGoals),
    [followedGoals]
  );

  // revalidating the user`s followed goal after login
  useEffect(() => {
    if (isLoggedIn) {
      fetchFollowedGoals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Header will be sticky for web and mobile web header will be scrollable
  useEffect(() => {
    const layoutHeader = document.getElementsByTagName('header')[0];
    if (layoutHeader) {
      if (isMobile) layoutHeader.style.position = 'relative';
      else layoutHeader.style.position = 'sticky';
    }
  }, [isMobile]);

  // Hiding the header when user start searching for mobile web
  useEffect(() => {
    if (isMobile) {
      const layoutHeader = document.getElementsByTagName('header')[0];
      if (layoutHeader)
        layoutHeader.style.display = searchInFocus ? 'none' : 'block';
    }
  }, [searchInFocus, isMobile]);

  const showError = ({ res }) => {
    showSnackbar({
      message:
        res.error.message ||
        'Some error occurred. Please refresh the page and try again.',
      type: 'error'
    });
  };

  const sendOnboardingGoalSelectedEvent = ({ goalData, ...props }) => {
    segment.track(ONBOARDING_GOAL_SELECTED, {
      ...props,
      is_logged_in: isLoggedIn,
      is_subscription_active: hasActiveSubscriptionToGoal(
        goalData,
        goalData.uid
      ),
      last_primary_source_section: isOnboarding ? 'Explore' : 'Manage Goals',
      goal_selected_section: goalSelectedSection.current
    });
  };

  const sendOnboardingGoalGroupSelectedEvent = (goalGroup) => {
    segment.track(ONBOARDING_GOAL_GROUP_SELECTED, {
      goal_type: goalGroup?.type === 'k12' ? 'K12' : 'Competitive Exams',
      goal_group: goalGroup.name,
      is_logged_in: isLoggedIn,
      last_primary_source_section: 'Manage Goals'
    });
  };

  const createSetSelectedGoalAction = useUpdateSelectedGoal();

  const gotoSelectedGoal = (goal, routeObj) => {
    const route = routeObj || {
      href: '/goal/[goalSlug]/[goalUID]',
      as: `/goal/${goal.slug}/${goal.uid}`
    };
    createSetSelectedGoalAction(goal.uid);
    pushRoute(route.href, route.as);
  };

  const onLanguageSelect = async (languageCode, languageText, goal) => {
    const goalData = selectedGoal || goal;

    await followGoal(goalData.uid, true);
    await fetchFollowedGoals();
    const setGoalLanguageResponse = await setGoalLanguage(
      goalData.uid,
      languageCode
    );
    if (!setGoalLanguageResponse?.error?.error_coder) {
      const selectedGoalExtraData = {
        ...goalData,
        relativeLink: goalData.relative_link,
        label: goalData.name,
        value: goalData.uid,
        as: goalData.relative_link,
        href: '/goal/[goalSlug]/[goalUID]'
      };
      segment.track(ONBOARDING_LANGUAGE_SELECTED, {
        goal_uid: goalData.uid,
        goal_name: goalData.name,
        language: languageCode
      });
      sendOnboardingGoalSelectedEvent({
        goalData,
        goal_uid: goalData.uid,
        goal_name: goalData.name,
        language: languageCode,
        business_platform: getBusinessPlatform(goalData)
      });
      setDrawerState(false);
      setSelectedGoal(null);
      gotoSelectedGoal(selectedGoalExtraData);
    }
  };

  const goToGloPage = (goal) => {
    gotoSelectedGoal(goal, {
      href: '/goal/[goalSlug]/[goalUID]/preferences',
      as: `/goal/${goal.slug}/${goal.uid}/preferences`
    });
  };

  const addToMyGoals = async (goal, gloData) => {
    getBusinessPlatform(goal);
    const sendSegmentEventForGoal = () => {
      sendOnboardingGoalSelectedEvent({
        goalData: goal,
        goal_uid: goal.uid,
        goal_name: goal.name || '',
        language: null,
        business_platform: getBusinessPlatform(goal)
      });
    };

    if (gloData.isAvailable && isLoggedIn) {
      const pinResponse = await followGoal(goal.uid, true);

      if (pinResponse?.error) {
        showError({ res: pinResponse });
        return;
      }

      sendSegmentEventForGoal();

      const followedGaolResponse = await fetchFollowedGoals();

      if (followedGaolResponse?.error) {
        showError({ res: followedGaolResponse });
        return;
      }

      if (
        gloData.status !== goalLevelOnboardingStatus.COMPLETE &&
        !isMobile &&
        !goal.isUaIconGoal
      ) {
        goToGloPage(goal);
      }
    } else if (isLoggedIn) {
      if (goal.isUaIconGoal) {
        setSelectedGoal(goal);
        gotoSelectedGoal(goal);
        return;
      }

      const res = await fetchGoalLanguages(goal.uid);

      if (res?.error) {
        showError({ res });
        return;
      }

      // when language is only 'All'
      if (res?.length === 1 && res[0]?.code === 0) {
        setSelectedGoal(goal);
        setTimeout(() => onLanguageSelect(0, 'All', goal), 1000);
        return;
      }

      if (!goal.isUaIconGoal) {
        setDrawerState(true);
      }

      setSelectedGoal(goal);
    } else {
      // anonymous user
      sendSegmentEventForGoal();
      gotoSelectedGoal(goal);
    }
  };
  const onGoalCardClick = async ({
    goal,
    evtSource,
    isMyGoal,
    isFollowedGoal,
    isSubscribedGoal
  }) => {
    goalSelectedSection.current = evtSource;
    const gloData = goal.goalLevelOnboarding || {
      isAvailable: goal.isGoalOnboardingAvailable || false,
      status: goal.userOnboardingStatus || -1
    };
    if (onGoalSelection) {
      onGoalSelection(goal);
    } else if (isMyGoal || isFollowedGoal || isSubscribedGoal) {
      if (
        gloData.isAvailable &&
        gloData.status !== goalLevelOnboardingStatus.COMPLETE &&
        !goal.isUaIconGoal
      )
        goToGloPage(goal);
      else {
        const goalRouteObj = {
          href: '/goal/[goalSlug]/[goalUID]',
          as: `/goal/${goal.slug}/${goal.uid}`
        };
        if (isSubscribedGoal && !goal.isUaIconGoal) {
          goalRouteObj.href += '/planner';
          goalRouteObj.as += '/planner';
        }
        gotoSelectedGoal(goal, goalRouteObj);
      }
    } else addToMyGoals(goal, gloData);
    setHasInteracted(true);
    const { subscription, expiredAt, timeRemaining } =
      getSubscription(latestUserSubscriptions, goal.uid) || {};
    segment.track(GOAL_SEARCH_RESULT_CLICKED, {
      goal_name: goal.name,
      goal_uid: goal.uid,
      last_primary_source_section: 'goal_onboarding',
      is_subscription_active: timeRemaining > 0,
      result_section_clicked: goal.name,
      search_stiring_entered: goalSearchValue,
      search_term: goalSearchValue,
      search_term_length: goalSearchValue.length,
      subscription_duration: subscription?.duration || 'NA',
      subscription_end_date: expiredAt,
      tap_id: tpID.current,
      business_platform: getBusinessPlatform(goal)
    });
  };
  useEffect(() => {
    document.querySelector('html').style.scrollBehavior = 'smooth';
    return () => {
      document.querySelector('html').style.scrollBehavior = 'unset';
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      scrollTop.current =
        window?.pageYOffset || document?.documentElement?.scrollTop;
      if (
        currentRefIndex < goalGroupRefs.current.length &&
        scrollTop.current >= lastScrollTop.current
      ) {
        // down scroll
        const nextRef = goalGroupRefs.current[currentRefIndex + 1];
        if (nextRef?.current?.getBoundingClientRect().y <= 200) {
          setCurrentRefIndex(currentRefIndex + 1);
        }
      } else if (currentRefIndex > 0) {
        // up scroll
        const previousRef = goalGroupRefs.current[currentRefIndex - 1];
        const currentRef = goalGroupRefs.current[currentRefIndex];
        if (
          previousRef?.current?.getBoundingClientRect().bottom > 200 &&
          currentRef?.current?.getBoundingClientRect().y > 300
        ) {
          setCurrentRefIndex(currentRefIndex - 1);
        }
      }
      lastScrollTop.current = scrollTop.current <= 0 ? 0 : scrollTop.current;
    };
    window.addEventListener('scroll', handleScroll, false);
    return () => {
      scrollTop.current = 0;
      window.removeEventListener('scroll', handleScroll);
    };
  }, [currentRefIndex]);

  useEffect(() => {
    const target = document.getElementById('page-header');
    const searchWrapper = document.getElementById('search-wrapper');
    const searchWrapperHeight = searchWrapper.clientHeight;
    const navContainer = document.getElementById('nav-container');

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: [0.0, 0.1, 0.2, 0.3, 0.4, 0.6, 0.7, 0.8, 0.9, 1.0]
    };

    const callback = (entries) => {
      entries.forEach((entry) => {
        searchWrapper.firstChild.style.transform = `translate(0, -${
          searchWrapperHeight / 2
        }px)`;
        if (isMobile) {
          if (entry.intersectionRatio < 0.3) {
            searchWrapper.firstChild.style.transform = 'translate(0, 0)';
            searchWrapper.style.borderBottom =
              'solid 0.5px var(--color-divider)';
            searchWrapper.style.backgroundColor = 'var(--color-base-0)';
            navContainer.style.top = '0px';
          } else {
            searchWrapper.firstChild.style.transform = `translate(0, -${
              searchWrapperHeight / 2
            }px)`;
            searchWrapper.style.borderBottom = 'none';
            searchWrapper.style.backgroundColor = 'transparent';
            navContainer.style.top = `-${searchWrapperHeight / 2}px`;
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (entry.intersectionRatio < 0.3) {
            searchWrapper.firstChild.style.transform = 'translate(0, 0)';
          } else {
            searchWrapper.firstChild.style.transform = `translate(0, -${
              searchWrapperHeight / 2
            }px)`;
            navContainer.style.top = `-${searchWrapperHeight}px`;
          }
        }
      });
    };
    const observer = new IntersectionObserver(callback, options);

    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [isMobile]);

  // scroll at initial loading as per topologyUId
  useEffect(() => {
    if (topologyUid) {
      const currentSelectedGoalGroup = allGoalGroups.length
        ? allGoalGroups.findIndex((item) => item.uid === topologyUid)
        : 0;
      setIsLoadingFirst(true);
      setCurrentRefIndex(currentSelectedGoalGroup);
    }
  }, [topologyUid, allGoalGroups]);

  useEffect(() => {
    const scrollUsingTopology = () => {
      const currentRef = goalGroupRefs.current[currentRefIndex];
      if (currentRef && currentRef.current) {
        currentRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
      setIsLoadingFirst(false);
    };
    if (topologyUid && isLoadingFirst) {
      scrollUsingTopology();
    }
  }, [currentRefIndex, topologyUid, isLoadingFirst]);

  const filterOptions = (value) => {
    if (!value) {
      return allGoalGroups;
    }
    const newGroupData = [];
    searchCount.current = 0;
    allGoalGroups.forEach((goalGroup) => {
      let goalGroupMatched = false;
      if (goalGroup.children.length) {
        if (isValidGoalOption(value, goalGroup, goalSynonyms)) {
          goalGroupMatched = true;
        }
        const goals = [];
        goalGroup.children.forEach((goal) => {
          if (isValidGoalOption(value, goal, goalSynonyms)) {
            searchCount.current += 1;
            goals.push(goal);
          }
        });
        if (goals.length) {
          newGroupData.push({ ...goalGroup });
          newGroupData[newGroupData.length - 1].children = goals;
        } else if (goalGroupMatched) {
          newGroupData.push(goalGroup);
          searchCount.current += goalGroup.children.length;
        }
      }
    });
    return newGroupData;
  };
  const headerImageURL = isDarkModeOn
    ? 'goal/managegoal-test-prep-dark-new.svg'
    : 'goal/managegoal-test-prep-light-new.svg';
  const hasMultipleGoalGroups = allGoalGroups.length > 1;

  const handleSearch = (value) => setGoalSearchValue(value);
  const searchValueChange = useMemo(
    () =>
      debounce((value) => {
        const filteredOptions = filterOptions(value);
        setFilteredGoalOptions(filteredOptions);
        handleSearch(value);
        if (filteredOptions.length === 0) {
          segment.track(GOAL_SEARCH_NO_RESULT, {
            last_primary_source_section: 'goal_onboarding',
            search_stiring_entered: value
          });
        }
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goalSynonyms]
  );

  const onBlurHandler = () => {
    setOverlay(false);
  };

  const onFocusHandler = () => {
    setOverlay(true);
    if (isMobile) {
      setSearchInFocus(true);
      const LayoutHeader = document.getElementsByTagName('header')[0];
      if (LayoutHeader)
        LayoutHeader.scrollIntoView({
          behavior: 'auto',
          block: 'end'
        });
    }
    setHasInteracted(true);
    tpID.current = uuid();
    segment.track(GOAL_SEARCH_SEARCH_CLICKED, {
      last_primary_source_section: 'goal_onboarding',
      tap_id: tpID.current
    });
  };

  useEffect(() => {
    if (hasInteracted) {
      setShowSearchTooltip(false);
    }
  }, [hasInteracted]);

  const onGoalGroupClick = () => setHasInteracted(true);

  useEffect(() => {
    const initialScrollPos = window.scrollY;
    const timeoutId = setTimeout(() => {
      if (!hasInteracted) setShowSearchTooltip(true);
    }, 1000);
    const handleScroll = () => {
      if (initialScrollPos !== window.scrollY) {
        const tip = document.querySelector("div[role='tooltip']");
        setHasInteracted(true);
        if (tip) {
          tip.classList.add('hide');
          setTimeout(() => {
            setShowSearchTooltip(false);
          }, 500);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [hasInteracted]);

  const closeTooltip = () => {
    setShowSearchTooltip(false);
  };

  const searchTooltip = <SearchTooltip closeTooltip={closeTooltip} />;

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.scrollY + window.innerHeight / 2 >
          document.documentElement.scrollHeight / 2 &&
        !scrollTracked.current
      ) {
        segment.track(ONBOARDING_GOAL_PAGE_SCROLLED, {
          is_logged_in: isLoggedIn,
          last_primary_source_section: 'goal_onboarding',
          percentage_scrolled: 50
        });
        scrollTracked.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, false);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isLoggedIn]);

  const ddOptions = useMemo(() => {
    const options = [];
    filteredGoalOptions.forEach((goalGroup) => {
      if (goalGroup.name) {
        options.push({
          ...goalGroup,
          value: goalGroup.uid,
          label: goalGroup.name,
          disable: true
        });
        goalGroup.children.forEach((goal) => {
          options.push({
            ...goal,
            value: goal.uid,
            label: goal.name
          });
        });
      }
    });
    return options;
  }, [filteredGoalOptions]);

  const handleBack = () => {
    setSearchInFocus(false);
    searchValueChange('');
    searchBarRef.current.clear();
    searchBarRef.current.blur();
  };

  const goalNavGroups = useMemo(() => {
    if (isMobile && searchInFocus)
      return goalSearchValue ? filteredGoalOptions : popularGoalGroup;
    if (isMobile) return [...popularGoalGroup, ...filteredGoalOptions];
    return filteredGoalGroups;
  }, [
    filteredGoalGroups,
    isMobile,
    popularGoalGroup,
    filteredGoalOptions,
    searchInFocus,
    goalSearchValue
  ]);

  const hasFilteredGoalOptions = !!filteredGoalOptions.length;
  const hasGoalGroups = isMobile
    ? hasFilteredGoalOptions
    : !!filteredGoalGroups.length;

  return (
    <div id="manageGoal-page">
      {isMobile && searchInFocus && (
        <Header searchInFocus={searchInFocus} handleBack={handleBack} />
      )}

      <PageHeader
        id="page-header"
        $isDarkModeOn={isDarkModeOn}
        $hide={searchInFocus}
        $isFreeTrial={isFreeTrial}
      >
        <Overlay show={showOverlay} />

        <WrapperWithBg imageURL={headerImageURL} searchInFocus={searchInFocus}>
          {!searchInFocus && (
            <InnerWrapper>
              {!isFreeTrial && (
                <HeaderText id="header-text" color="var(--color-text-primary)">
                  Choose your goal
                </HeaderText>
              )}
              {isFreeTrial && (
                <HeaderText>Select a goal to activate trial</HeaderText>
              )}
            </InnerWrapper>
          )}
        </WrapperWithBg>
      </PageHeader>

      <GoalSearchBarWrapper id="search-wrapper" $searchInFocus={searchInFocus}>
        <StyledTooltip
          open={showSearchTooltip}
          title={searchTooltip}
          borderRadius={Spacings.SPACING_2B}
          applyMobileTransform={false}
          $isFreeTrial={isFreeTrial}
        >
          <SearchWrapper
            role="search-goal"
            showBorder={!showOverlay}
            showBorderRadius={isMobile || !showOverlay}
            $isFreeTrial={isFreeTrial}
          >
            <SearchTab
              placeholder="Search for your goal"
              options={goalSearchValue ? ddOptions : popularGoalOptions}
              onChange={searchValueChange}
              onFocus={onFocusHandler}
              onBlur={onBlurHandler}
              ref={searchBarRef}
              DropdownOptionList={DropdownOptionList}
              dropdownOptionProps={{
                goalSearchValue,
                onGoalCardClick,
                mappedFollowedGoals
              }}
            />
          </SearchWrapper>
        </StyledTooltip>
      </GoalSearchBarWrapper>
      <Container
        id="nav-container"
        $searchInFocus={searchInFocus}
        className={!filteredGoalGroups.length ? 'no-result' : ''}
        $isFreeTrial={isFreeTrial}
      >
        {hasGoalGroups ? (
          <>
            {hasMultipleGoalGroups && (
              <GoalGroupNavWrapper>
                {!isMobile && (
                  <WebGoalGroups
                    sendOnboardingGoalGroupSelectedEvent={
                      sendOnboardingGoalGroupSelectedEvent
                    }
                    setCurrentRefIndex={setCurrentRefIndex}
                    currentRefIndex={currentRefIndex}
                    onGoalGroupClick={onGoalGroupClick}
                    onGoalCardClick={onGoalCardClick}
                    goalGroupRefs={goalGroupRefs}
                    allGoalGroups={allGoalGroups}
                  />
                )}
              </GoalGroupNavWrapper>
            )}
            {hasFilteredGoalOptions && (
              <MobileGoalGroupNavWrapper>
                {isMobile && hasMultipleGoalGroups && (
                  <MobileGoalGroupNav
                    goalGroups={goalNavGroups}
                    onGoalCardClick={onGoalCardClick}
                    followedGoals={followedGoals}
                    highlightValue={goalSearchValue}
                    accordionOpen={searchInFocus}
                    onGoalGroupClick={onGoalGroupClick}
                    searchInFocus={searchInFocus}
                  />
                )}
              </MobileGoalGroupNavWrapper>
            )}
            {drawerState && (
              <LanguageSelectionDrawer
                languages={goalLanguages}
                onLanguageSelect={onLanguageSelect}
                drawerState={drawerState}
                setShowLanguages={setDrawerState}
                hideCloseButton
                heading="Pick a goal Language"
                subHeading="Courses in the preferred language will be shown first"
              />
            )}
          </>
        ) : (
          <NoResultFound />
        )}
      </Container>
    </div>
  );
};

export default ManageGoals;

ManageGoals.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  goalGroupsData: PropTypes.arrayOf(PropTypes.any),
  isOnboarding: PropTypes.bool,
  onGoalSelection: PropTypes.func,
  isFreeTrial: PropTypes.bool
};

ManageGoals.defaultProps = {
  goalGroupsData: [],
  isOnboarding: false,
  onGoalSelection: null,
  isFreeTrial: false
};
